/**
 * Maintenance Tasks (task type definitions) hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMaintenanceTasksRelayDirectQuery as UseMaintenanceTasksRelayDirectQuery,
  MaintenanceTaskFilterInput,
  MaintenanceTaskSortInput,
} from './__generated__/useMaintenanceTasksRelayDirectQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Direct query for maintenance tasks list (task type definitions for dropdowns)
 */
const MaintenanceTasksDirectQuery = graphql`
  query useMaintenanceTasksRelayDirectQuery(
    $first: Int
    $after: String
    $filter: MaintenanceTaskFilterInput
    $sort: [MaintenanceTaskSortInput!]
  ) {
    listMaintenanceTasks(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          description
          taskType
          timeEstimate
          archivedOn
          createdOn
          systemId
          systemOldId
          organizationId
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MaintenanceTaskFilterInput, MaintenanceTaskSortInput };

export interface MaintenanceTaskGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  description: string;
  taskType?: string | null;
  timeEstimate?: number | null;
  archivedOn?: string | null;
  createdOn: string;
  systemId?: string | null;
  systemOldId?: string | null;
  organizationId?: string | null;
}

export interface MaintenanceTaskQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  includeArchived?: boolean;
  includeGlobal?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook that provides React Query-like interface for maintenance tasks data.
 * Replaces useAllTask. Does NOT use Suspense.
 */
export function useMaintenanceTasksRelayQuery(
  filter?: MaintenanceTaskQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceTaskSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<MaintenanceTaskGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 250;
  const sort = useMemo(
    () =>
      options?.sort ?? [
        { field: 'NAME' as const, order: 'ASC' as const },
      ],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildTaskRelayFilter(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result =
          await fetchQuery<UseMaintenanceTasksRelayDirectQuery>(
            relayEnvironment,
            MaintenanceTasksDirectQuery,
            variables
          ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMaintenanceTasks?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as MaintenanceTaskGraphQL[]);
          setCount(result.listMaintenanceTasks?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch maintenance tasks')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildTaskRelayFilter(
  filter?: MaintenanceTaskQueryFilter
): MaintenanceTaskFilterInput | undefined {
  if (!filter) {
    // Default: active only
    return { archivedOn: { isNil: true } };
  }

  const archivedFilter: MaintenanceTaskFilterInput = !filter.includeArchived
    ? { archivedOn: { isNil: true } }
    : {};

  // Build OR conditions: system tasks OR organization tasks OR global tasks
  if (filter.includeGlobal) {
    const orConditions: MaintenanceTaskFilterInput[] = [];

    if (filter.systemOldId) {
      orConditions.push({ systemOldId: { eq: filter.systemOldId } });
    }
    if (filter.systemId) {
      orConditions.push({ systemId: { eq: filter.systemId } });
    }
    if (filter.organizationId) {
      orConditions.push({ organizationId: { eq: filter.organizationId } });
    }

    // Global tasks: no system and no organization
    orConditions.push({
      and: [
        { systemId: { isNil: true } },
        { organizationId: { isNil: true } },
      ],
    });

    return {
      ...archivedFilter,
      or: orConditions,
    };
  }

  const relayFilter: MaintenanceTaskFilterInput = { ...archivedFilter };

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildTaskRelayFilter };
